import {getCurrentAuthToken} from './data/pocketbaseClient';

export const APP_CONFIG = {
  appName: 'InvoiceFlow Studio',
  version: '0.1.0',
  mode: import.meta.env.VITE_POCKETBASE_URL ? 'PocketBase Mode' : 'Mock Mode',
  targetFileCount: 31,
  hardFileCap: 36,
  features: {
    externalAutomation: true,
    pocketbase: Boolean(import.meta.env.VITE_POCKETBASE_URL),
  },
  pocketbase: {
    url: import.meta.env.VITE_POCKETBASE_URL || null,
    isConfigured: Boolean(import.meta.env.VITE_POCKETBASE_URL),
  }
};

export const EMAIL_RELAY_URL = import.meta.env.VITE_EMAIL_RELAY_URL || '';

// Browser-only relay progress event emitter
const emitRelayProgress = (channel: 'email' | 'sms', action: string, phase: string, message?: string) => {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(new CustomEvent('invoiceflow:relay-progress', {
    detail: { channel, action, phase, message: message || '' }
  }));
};

export interface RelayDispatchResult {
  success: boolean;
  status?: string;
  message?: string;
  processedCount?: number;
  updatedLogs?: number;
  checkedMessages?: number;
  queuedJobsFound?: number;
  queuedReminders?: number;
  dispatched?: number;
  queued?: number;
  failed?: number;
  skipped?: number;
  errors?: string[];
}

async function dispatchRelayAction(
  action: 'processEmailJob' | 'processQueue' | 'processSmsQueue' | 'generatePaymentReminderQueue' | 'syncEmailDeliveryStatuses',
  emailJobId?: string,
  silent = false
): Promise<RelayDispatchResult> {
  const channel = action === 'processSmsQueue' ? 'sms' : 'email';
  if (!silent) emitRelayProgress(channel, action, 'preparing', 'Preparing...');

  if (!EMAIL_RELAY_URL) {
    if (!silent) emitRelayProgress(channel, action, 'queued', 'Email relay URL is not configured. The records remain queued.');
    return {
      success: false,
      status: 'queued',
      message: 'Email relay URL is not configured. The records remain queued.'
    };
  }

  const userToken = getCurrentAuthToken();
  if (!userToken) {
    if (!silent) emitRelayProgress(channel, action, 'error', 'Your InvoiceFlow login session is not available. Sign in again before manually processing the queue.');
    throw new Error(
      'Your InvoiceFlow login session is not available. Sign in again before manually processing the queue.'
    );
  }

  if (!silent) emitRelayProgress(channel, action, 'sending', 'Sending request...');
  const response = await fetch(
    EMAIL_RELAY_URL,
    {
      method: 'POST',
      headers: { 'Content-Type': 'text/plain;charset=utf-8' },
      body: JSON.stringify({ action, emailJobId, userToken })
    }
  );

  const data = await response.json();

  if (!response.ok || !data.success) {
    const errorMsg = data.message || 'Relay request failed.';
    if (!silent) emitRelayProgress(channel, action, 'error', errorMsg);
    throw new Error(
      errorMsg
    );
  }

  if (typeof window !=='undefined') {
    window.dispatchEvent(new CustomEvent('invoiceflow:repository-cache-clear'));
  }
  if (!silent) emitRelayProgress(channel, action, 'success', data.message || 'Request completed successfully.');
  return data;
}

export async function dispatchEmailJob(
  emailJobId: string
): Promise<RelayDispatchResult> {
  return dispatchRelayAction(
    'processEmailJob',
    emailJobId
  );
}

export async function dispatchEmailQueue(): Promise<RelayDispatchResult> {
  return dispatchRelayAction(
    'processQueue'
  );
}

export async function dispatchSmsQueue(): Promise<RelayDispatchResult> {
  return dispatchRelayAction(
    'processSmsQueue'
  );
}

export async function dispatchPaymentReminderQueue(): Promise<RelayDispatchResult> {
  return dispatchRelayAction(
    'generatePaymentReminderQueue'
  );
}

export async function dispatchEmailDeliverySync(): Promise<RelayDispatchResult> {
  return dispatchRelayAction(
    'syncEmailDeliveryStatuses',
    undefined,
    true
  );
}

// Standalone manual SMS through the existing authenticated Apps Script URL.
// The TextLink key and sender SIM stay exclusively in Apps Script properties.
// Unlike the automatic SMS flow, this creates no PocketBase records.
export interface QuickSmsRequest {
  phone: string;
  message: string;
  requestId: string;
}

export interface QuickSmsResponse {
  success: boolean;
  status: string;
  message?: string;
  duplicate?: boolean;
  providerMessageId?: string;
}

export async function dispatchQuickSms(payload: QuickSmsRequest): Promise<QuickSmsResponse> {
  if (!EMAIL_RELAY_URL) {
    throw new Error('The existing Apps Script relay URL is not configured.');
  }
  const userToken = getCurrentAuthToken();
  if (!userToken) {
    throw new Error('Your InvoiceFlow login expired. Sign in again before sending.');
  }
  let response: Response;
  try {
    response = await fetch(EMAIL_RELAY_URL, {
      method: 'POST',
      headers: {'Content-Type': 'text/plain;charset=utf-8'},
      body: JSON.stringify({action: 'sendQuickSms', userToken, ...payload})
    });
  } catch {
    throw new Error('Connection interrupted. Your message might have been accepted. Check TextLink before attempting another SMS.');
  }
  let data: QuickSmsResponse;
  try {
    data = await response.json();
  } catch {
    throw new Error('Unable to confirm the SMS response. Check TextLink before sending again.');
  }
  if (!response.ok || !data.success) {
    throw new Error(data.message || 'TextLink did not confirm the SMS. Check TextLink before retrying.');
  }
  return data;
}